import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { googleIntegrationService, userService } from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import {
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className="ml-2 p-1.5 text-gray-400 hover:text-primary-600 transition-colors" title="Copy">
      {copied ? <CheckCircleIcon className="h-4 w-4 text-green-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
    </button>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 break-all">
        <span className="flex-1">{value}</span>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

// ── Settings Panel (super_admin only) ─────────────────────────────────────────
function GoogleSettings({ queryClient }) {
  const { data, isLoading } = useQuery('googleSettings', googleIntegrationService.getSettings, { retry: false });
  const settings = data?.data?.data || {};

  const { data: usersData } = useQuery('assignableUsers', userService.getAssignableUsers);
  const assignableUsers = usersData?.data?.data?.users || usersData?.data?.users || [];

  const [routeToUserId, setRouteToUserId] = useState('');

  // Sync routeToUserId from fetched settings
  React.useEffect(() => {
    if (settings.routeToUserId) setRouteToUserId(settings.routeToUserId);
  }, [settings.routeToUserId]);

  const saveMutation = useMutation(
    () => googleIntegrationService.saveSettings({ routeToUserId: routeToUserId || null }),
    {
      onSuccess: () => {
        toast.success('Settings saved');
        queryClient.invalidateQueries('googleSettings');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
    }
  );

  const regenerateMutation = useMutation(googleIntegrationService.regenerateKey, {
    onSuccess: () => {
      toast.success('Webhook key regenerated');
      queryClient.invalidateQueries('googleSettings');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to regenerate'),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Google Ads Webhook Configuration</h2>
        <p className="text-sm text-gray-500 mt-0.5">Copy the Webhook URL and Key into your Google Ads Lead Form settings</p>
      </div>

      <div className="px-5 py-5 space-y-4">
        {/* Webhook URL */}
        <ReadOnlyField label="Webhook URL" value={settings.webhookUrl || '—'} />

        {/* Webhook Key */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Webhook Key (google_key)</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 break-all">
              <span className="flex-1">{settings.webhookKey || '—'}</span>
              {settings.webhookKey && <CopyButton value={settings.webhookKey} />}
            </div>
            <button
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isLoading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              title="Generate new key"
            >
              <ArrowPathIcon className={`h-4 w-4 ${regenerateMutation.isLoading ? 'animate-spin' : ''}`} />
              Regenerate
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">If you regenerate, update the key in your Google Ads lead form too.</p>
        </div>

        {/* Default Route To */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Route All Google Leads To</label>
          <select
            value={routeToUserId}
            onChange={e => setRouteToUserId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">— Auto (first active admin) —</option>
            {assignableUsers.map(u => (
              <option key={u._id} value={u._id}>
                {u.firstName} {u.lastName} ({u.role?.replace('_', ' ')})
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">All incoming Google Ads leads will be assigned to this person.</p>
        </div>

        <div className="pt-1">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isLoading}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isLoading ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── How to Set Up (instructions panel) ───────────────────────────────────────
function SetupInstructions() {
  const steps = [
    { step: 1, title: 'Copy the Webhook URL and Key above' },
    { step: 2, title: 'Go to Google Ads → Campaigns → Select a campaign' },
    { step: 3, title: 'Click on Assets → Lead Form → Edit the lead form' },
    { step: 4, title: 'Scroll to "Webhook integration" section' },
    { step: 5, title: 'Paste the Webhook URL and Key, then save' },
    { step: 6, title: 'Google will verify the webhook automatically' },
    { step: 7, title: 'New leads will appear in LMS within seconds' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">How to Set Up</h2>
        <p className="text-sm text-gray-500 mt-0.5">Follow these steps in Google Ads to start receiving leads</p>
      </div>
      <div className="px-5 py-5">
        <ol className="space-y-3">
          {steps.map(({ step, title }) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center mt-0.5">
                {step}
              </span>
              <span className="text-sm text-gray-700">{title}</span>
            </li>
          ))}
        </ol>

        <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-800">Important Notes</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-700 list-disc list-inside">
            <li>The webhook key is used to verify requests come from Google</li>
            <li>If you regenerate the key, update it in Google Ads immediately</li>
            <li>Duplicate leads (same phone within 24 hours) are automatically skipped</li>
            <li>Google Ads lead forms must be active for leads to flow in</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export const GoogleIntegration = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Google Ads Integration</h1>
        <p className="text-sm text-gray-500 mt-1">Automatically import leads from Google Ads Lead Form campaigns</p>
      </div>

      {isSuperAdmin ? (
        <>
          <GoogleSettings queryClient={queryClient} />
          <SetupInstructions />
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-12 text-center">
          <p className="text-gray-500 text-sm">Contact your Super Admin to configure Google Ads integration.</p>
        </div>
      )}
    </div>
  );
};
